import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import { Flex, Text } from '@chakra-ui/react';
import Input from '../input/input';
import './datepicker-component.scss';
import { styles } from './datepicker-component.styles';
import { DatePickerHeader } from './datepicker-component-header';
import * as dateUtils from '../../core/utils/date';
import { InputVariant } from '../../core/enums';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
const TEXT_CONSTANTS = {
    DAY: 'day',
    MONTH: 'month',
    YEAR: 'year'
};
const getDisplayDate = (startDate, endDate, showRange)=>{
    if (!startDate) {
        return null;
    }
    const formattedStartDate = dateUtils.convertToDateFormat(startDate);
    let displayDate = formattedStartDate;
    if (showRange) {
        displayDate = `${formattedStartDate} to ${formattedStartDate}`;
    }
    if (endDate) {
        const formattedEndDate = dateUtils.convertToDateFormat(endDate);
        displayDate = `${formattedStartDate} to ${formattedEndDate}`;
    }
    return displayDate;
};
const DatePickerComponent = (props)=>{
    const { onSelection, placeholder = 'Select Date', label, startDate, endDate, showRange = true, classNames, selectsRange = true, iconRight, iconLeft, showActiveBackground } = props;
    const [_startDate, setStartDate] = useState(startDate);
    const [_endDate, setEndDate] = useState(endDate);
    const [datePickerType, setDatePickerType] = useState(TEXT_CONSTANTS.DAY);
    const [closeDatePicker, setCloseDatePicker] = useState(true);
    const [isDatePickerOpen, setDatePickerOpen] = useState(false);
    useEffect(()=>{
        setStartDate(startDate);
        setEndDate(endDate);
    }, [
        startDate,
        endDate
    ]);
    const onChange = (dates)=>{
        let start = null;
        let end = null;
        if (Array.isArray(dates)) {
            [start, end] = dates;
        } else {
            start = dates;
        }
        /** show months picker when year is selected,
     * show days picker when month is selected and
     *  set start and end dates when date is selected
     * */ switch(datePickerType){
            case TEXT_CONSTANTS.DAY:
                {
                    setStartDate(start);
                    setEndDate(end);
                    onSelection && onSelection(start, end);
                    break;
                }
            case TEXT_CONSTANTS.MONTH:
                {
                    setDatePickerType(TEXT_CONSTANTS.DAY);
                    /** Close the datepicker on select under the following conditions
         * Selecting a single date (or) the start and end dates in a range picker
         */ if (!selectsRange && dates) {
                        setCloseDatePicker(true);
                    } else if (selectsRange && Array.isArray(dates)) {
                        if (dates[0] && dates[1]) {
                            setCloseDatePicker(true);
                        }
                    } else {
                        setCloseDatePicker(false);
                    }
                    break;
                }
            case TEXT_CONSTANTS.YEAR:
                {
                    setDatePickerType(TEXT_CONSTANTS.MONTH);
                    break;
                }
            default:
                {
                    console.warn(`Unsupported datepicker change ${datePickerType}`);
                }
        }
    };
    const handleClearDate = ()=>{
        setStartDate(null);
        setEndDate(null);
        setDatePickerType(TEXT_CONSTANTS.DAY);
        onSelection && onSelection(null, null);
    };
    const onDayPickerMonthClick = ()=>{
        closeDatePicker && setCloseDatePicker(false);
        setDatePickerType(TEXT_CONSTANTS.MONTH);
    };
    const onHeaderYearClick = ()=>{
        closeDatePicker && setCloseDatePicker(false);
        setDatePickerType(TEXT_CONSTANTS.YEAR);
    };
    const handleCalendarClose = ()=>{
        isDatePickerOpen && setDatePickerOpen(false);
        setDatePickerType(TEXT_CONSTANTS.DAY);
    };
    const handleInputClick = ()=>{
        !isDatePickerOpen && setDatePickerOpen(true);
    };
    const isClearBtnDisabled = !Boolean(_startDate || _endDate);
    const classes = styles();
    const popperContainer = ({ children })=>/*#__PURE__*/ createPortal(children, document.body);
    return /*#__PURE__*/ _jsx(DatePicker, {
        className: "datepicker-component",
        placeholderText: placeholder,
        popperContainer: popperContainer,
        popperProps: {
            strategy: 'fixed'
        },
        selected: _startDate,
        startDate: _startDate,
        endDate: _endDate,
        onChange: onChange,
        ...selectsRange && {
            selectsRange: true
        },
        shouldCloseOnSelect: closeDatePicker,
        dateFormat: dateUtils.FORMATS.DATE,
        onCalendarClose: handleCalendarClose,
        customInput: _startDate && _endDate || !selectsRange ? /*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsx(Flex, {
                alignItems: 'center',
                children: /*#__PURE__*/ _jsx(Input, {
                    autoComplete: "off",
                    iconRight: iconRight,
                    iconLeft: iconLeft,
                    variant: InputVariant.TEXT,
                    value: getDisplayDate(_startDate, _endDate, showRange) ?? label,
                    placeholder: placeholder,
                    "data-testid": "custom-date-input",
                    classes: {
                        input: isDatePickerOpen && showActiveBackground ? classes.selectedInput : classNames?.input ?? classes.input,
                        formLabel: classNames?.formLabel ?? classes.formLabel,
                        leftIcon: classNames?.leftIcon ?? classes.leftIcon,
                        rightIcon: classNames?.rightIcon
                    },
                    onClick: handleInputClick
                })
            })
        }) : /*#__PURE__*/ _jsxs("div", {
            style: classes.inputPlaceholder,
            children: [
                /*#__PURE__*/ _jsx(BiomeIcon, {
                    iconName: IconName.Calendar,
                    iconSize: "16",
                    iconClass: "placeholderCalendar",
                    titleText: "Service Date"
                }),
                /*#__PURE__*/ _jsx(Text, {
                    sx: classes.defaultText,
                    children: "Service Date"
                })
            ]
        }),
        showYearPicker: TEXT_CONSTANTS.YEAR === datePickerType,
        showMonthYearPicker: TEXT_CONSTANTS.MONTH === datePickerType,
        showFourColumnMonthYearPicker: true,
        renderCustomHeader: ({ monthDate, date, decreaseMonth, increaseMonth, decreaseYear, increaseYear })=>/*#__PURE__*/ _jsxs("div", {
                children: [
                    datePickerType === TEXT_CONSTANTS.DAY && /*#__PURE__*/ _jsx(DatePickerHeader, {
                        onLeftArrowClick: decreaseMonth,
                        onRightArrowClick: increaseMonth,
                        monthDate: monthDate,
                        onMonthClick: onDayPickerMonthClick,
                        onYearClick: onHeaderYearClick,
                        dayPicker: true,
                        isClearBtnDisabled: isClearBtnDisabled,
                        handleClearDate: handleClearDate
                    }),
                    datePickerType === TEXT_CONSTANTS.YEAR && /*#__PURE__*/ _jsx(DatePickerHeader, {
                        onLeftArrowClick: decreaseYear,
                        onRightArrowClick: increaseYear,
                        yearRange: dateUtils.getYearRange(date),
                        yearPicker: true,
                        isClearBtnDisabled: isClearBtnDisabled,
                        handleClearDate: handleClearDate
                    }),
                    datePickerType === TEXT_CONSTANTS.MONTH && /*#__PURE__*/ _jsx(DatePickerHeader, {
                        onLeftArrowClick: decreaseYear,
                        onRightArrowClick: increaseYear,
                        selectedYear: date.getFullYear().toString(),
                        onMonthPickerYearClick: onHeaderYearClick,
                        monthPicker: true,
                        isClearBtnDisabled: isClearBtnDisabled,
                        handleClearDate: handleClearDate
                    })
                ]
            })
    });
};
DatePickerComponent.displayName = 'DatePickerComponent';
export { DatePickerComponent };
